import React from "react";
import ControlledField from "./ControlledField";
import FloatingNumber from "../../../../components/input/FloatingNumber";
import { formatCurrency } from "../../../../utils/format";

// Shared renderer for two-column field arrays.
// - fields: array of items with keys (key/camelKey/label/value/lock)
// - control, FloatingInput, cleanNumber: passed to Controllers
// - ignoreKeys: optional array of raw keys to filter out
export function renderPaymentItem(
  item,
  {
    control,
    FloatingInput,
    cleanNumber,
    toBaseKey,
    handleCurrencyBlur,
    onFormatted,
    index,
    parentName,
  }
) {
  const baseKey = toBaseKey(item.camelKey || item.key || item.label || "");
  const isRate =
    item.rate !== undefined && item.rate !== null && item.rate !== "";
  const idKey = String(
    (item.key || item.camelKey || item.label || baseKey || index || "") +
      "::" +
      String(item.value ?? "")
  ).replace(/\s+/g, "_");

  // Helper to produce namespaced RHF names when rendering from a useFieldArray
  const namespaced = (prop) =>
    parentName && typeof index === "number"
      ? `${parentName}[${index}].${prop}`
      : prop;
  //console salesPrice

  if (isRate) {
    const percentName = namespaced("rate");
    const dollarName = namespaced("value");
    return (
      <>
        <div
          className="calc-frm-fld form-group sign-of-percentage"
          key={`${percentName}-${idKey}`}
        >
          <ControlledField
            key={`${percentName}-${idKey}`}
            name={percentName}
            control={control}
            className="input-filled"
            defaultValue={item.rate ?? ""}
            InputComponent={FloatingNumber}
            label={`${item.label}`}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={onFormatted}
          />
        </div>
        <div
          className="calc-frm-fld form-group sign-of-amount"
          key={`${dollarName}-${idKey}`}
        >
          <ControlledField
            key={`${dollarName}-${idKey}`}
            name={dollarName}
            control={control}
            className="input-filled"
            defaultValue={item.value ?? ""}
            InputComponent={FloatingNumber}
            placeholder=""
            type="text"
            cleanNumber={cleanNumber}
            handleCurrencyBlur={handleCurrencyBlur}
            onFormatted={onFormatted}
          />
        </div>
      </>
    );
  }

  // fallback single input
  const name = namespaced("value") || baseKey;
  return (
    <>
      <div className="calc-frm-fld form-group" key={`${name}-${idKey}`}>
        <ControlledField
          key={`${name}-${idKey}`}
          name={name}
          control={control}
          defaultValue={item.value ?? ""}
          InputComponent={FloatingNumber}
          placeholder=""
          label={item.label}
          type="text"
          cleanNumber={cleanNumber}
          handleCurrencyBlur={handleCurrencyBlur}
          onFormatted={onFormatted}
        />
      </div>
      <div className="calc-frm-fld form-group" />
    </>
  );
}

export default function DynamicFieldsGrid({
  fields = [],
  control,
  cleanNumber,
  ignoreKeys = [],
  renderItem = null,
  getRowClass = null,
  fullRowPerItem = false,
  handleCurrencyBlur,
  onFormatted,
  parentName = null,
  getValues,
  setValue,
  salesPrice,
}) {
  if (!Array.isArray(fields) || fields.length === 0) return null;

  const ignoreSet = new Set(
    (ignoreKeys || []).map((k) => (k || "").toString())
  );

  const toBaseKey = (raw) => {
    const s = String(raw || "")
      .replace(/[-_ ]+(.)/g, (m, g1) => g1.toUpperCase())
      .replace(/[^a-zA-Z0-9]/g, "");
    return s.charAt(0).toLowerCase() + s.slice(1);
  };

  const visible = fields.filter(
    (f) => !ignoreSet.has((f.key || "").toString())
  );

  //console.log('DynamicFieldsGrid salesPrice:', salesPrice);

  if (fullRowPerItem) {
    //console.log('Rendering fullRowPerItem DynamicFieldsGrid');
    return (
      <>
        {visible.map((item, idx) => {
          const originalIndex = fields.indexOf(item);
          const rowClass =
            typeof getRowClass === "function"
              ? getRowClass(item, originalIndex)
              : "calc-frm-field aa";

          const idKey = String(
            (item.key || item.camelKey || item.label || originalIndex) +
              "::" +
              String(item.value ?? "")
          ).replace(/\s+/g, "_");
          return (
            <div
              className={rowClass}
              key={`dyn-fields-row-${originalIndex}-${idKey}`}
            >
              {typeof renderItem === "function"
                ? renderItem(item, {
                    control,
                    FloatingInput: FloatingNumber,
                    cleanNumber,
                    toBaseKey,
                    handleCurrencyBlur,
                    onFormatted,
                    index: originalIndex,
                    parentName,
                    getValues,
                    setValue,
                  })
                : // default single-column fallback when no renderItem provided
                  (() => {
                    const baseKey = toBaseKey(
                      item.camelKey || item.key || item.label || ""
                    );
                    // const isLocked = (item.lock || '').toString() === 'Y';
                    const origIndex = fields.indexOf(item);
                    const name = parentName
                      ? `${parentName}[${origIndex}].value`
                      : baseKey;
                    const idKey2 = String(
                      (item.key || item.camelKey || item.label || origIndex) +
                        "::" +
                        String(item.value ?? "")
                    ).replace(/\s+/g, "_");
                    console.log("Rendering full row item:");

                    return (
                      <>
                        <div
                          className="calc-frm-fld form-group sign-of-amount"
                          key={`${name}-${idKey2}`}
                        >
                          <ControlledField
                            key={`${name}-${idKey2}`}
                            name={name}
                            control={control}
                            defaultValue={item?.value ?? ""}
                            InputComponent={FloatingNumber}
                            placeholder=""
                            label={`${item.label}${
                              item.unit === "$" ? " $" : ""
                            }`}
                            type="text"
                            //inputProps={{ disabled: isLocked }}
                            cleanNumber={cleanNumber}
                            handleCurrencyBlur={handleCurrencyBlur}
                            onFormatted={onFormatted}
                          />
                        </div>
                        <div className="calc-frm-fld form-group" />
                      </>
                    );
                  })()}
            </div>
          );
        })}
      </>
    );
  }

  // retain index information so we can map into RHF array paths when parentName is provided
  const visibleWithIndex = visible.map((item, i) => ({ item, i }));
  const rows = [];
  for (let i = 0; i < visibleWithIndex.length; i += 2)
    rows.push(visibleWithIndex.slice(i, i + 2));

  return (
    <>
      {rows.map((pair, idx) => {
        const rowClass =
          typeof getRowClass === "function"
            ? getRowClass(pair, idx)
            : "calc-frm-field full-dvd";
        // build a stable row key derived from the items in the pair so changes to item identity force remount
        const rowIds = pair
          .map(({ item: it }) =>
            String(
              (it.key || it.camelKey || it.label || fields.indexOf(it)) +
                "::" +
                String(it.value ?? "")
            ).replace(/\s+/g, "_")
          )
          .join("-");
        return (
          <div className={rowClass} key={`dyn-fields-row-${idx}-${rowIds}`}>
            {pair.map(({ item, i }) => {
              // allow custom rendering per item if a renderItem hook is provided
              if (typeof renderItem === "function") {
                return renderItem(item, {
                  control,
                  FloatingInput: FloatingNumber,
                  cleanNumber,
                  toBaseKey,
                  handleCurrencyBlur,
                  onFormatted,
                  index: i,
                  parentName,
                  getValues,
                  setValue,
                });
              }

              const baseKey = toBaseKey(
                item.camelKey || item.key || item.label || ""
              );
              // const isLocked = (item.lock || '').toString() === 'Y';
              const origIndex = fields.indexOf(item);
              const valueName = parentName
                ? `${parentName}[${origIndex}].value`
                : baseKey;
              const amountName = parentName
                ? `${parentName}[${origIndex}].amount`
                : `${baseKey}Amount`;
              const idKey = String(
                (item.key || item.camelKey || item.label || origIndex) +
                  "::" +
                  String(item.value ?? "")
              ).replace(/\s+/g, "_");

              // determine which field (value or amount) we should render
              let renderName = valueName;
              let defaultVal = item?.value ?? "";

              // compute baseKey for special-case checks (e.g., transfer_tax)
              const specialBaseKey = toBaseKey(
                item.camelKey || item.key || item.label || ""
              );
              const rawKey = (item.key || "").toString();
              const isTransferTaxKey =
                rawKey === "transfer_tax" || specialBaseKey === "transferTax";

              // if unit is percent OR this is the transfer_tax key, calculate amount from salesPrice and write it
              if (item.unit === "%" || isTransferTaxKey) {
                const rateRaw = item?.value ?? "0";
                const salesRaw = salesPrice || "0";
                const sales =
                  Number(String(salesRaw).replace(/[^0-9.-]+/g, "")) || 0;
                const rate =
                  Number(String(rateRaw).replace(/[^0-9.-]+/g, "")) || 0;
                const amount = (sales * rate) / 1000;
                const formatted = formatCurrency(amount);
                try {
                  setValue(amountName, formatted, {
                    shouldDirty: true,
                    shouldValidate: false,
                  });
                } catch (err) {
                  // ignore setValue errors if not provided
                }
                renderName = amountName;
                defaultVal = formatted;
              } else if (item.unit === "$") {
                // for dollar unit, show the amount field
                renderName = valueName;
                defaultVal = item?.value ?? "0";
              }

              return (
                <div
                  className="calc-frm-fld form-group sign-of-amount"
                  key={`${renderName}-${idKey}`}
                >
                  <ControlledField
                    key={`${renderName}-${idKey}`}
                    name={renderName}
                    control={control}
                    defaultValue={defaultVal}
                    InputComponent={FloatingNumber}
                    placeholder=""
                    label={`${item.label}`}
                    type="text"
                    //inputProps={{ disabled: isLocked }}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={onFormatted}
                  />
                </div>
              );
            })}

            {pair.length === 1 && <div className="calc-frm-fld form-group" />}
          </div>
        );
      })}
    </>
  );
}
